/**
 * Keyword-based gallery photo ordering (community amenities first — signage
 * before exteriors — then unit interiors by room type). Ported from Price
 * Brothers Vantage PhotoGallery shared helpers — sorting only, no UI.
 */

export interface GalleryImage {
  image: string;
  alt_text?: string;
  title?: string;
  description?: string;
  source_page_urls?: string;
  original_url?: string;
}

/** Classify a gallery photo by its title — used to split community vs unit shots. */
export function classifyPhoto(photo: GalleryImage): 'apartment' | 'community' | 'other' {
  const title = (photo.title || '').toLowerCase().trim();
  if (title.includes('apartment')) return 'apartment';
  if (title.includes('community')) return 'community';
  return 'other';
}

function urlPathText(url?: string): string {
  if (!url) return '';
  try {
    return new URL(url).pathname.toLowerCase().replace(/[_-]+/g, ' ');
  } catch {
    return url.toLowerCase().split('?')[0].replace(/[_-]+/g, ' ');
  }
}

/** Human-authored labels — preferred over CDN path noise for subtype sorting. */
function photoLabelText(photo: GalleryImage): string {
  return [photo.title, photo.description, photo.alt_text]
    .filter((part): part is string => Boolean(part))
    .join(' ')
    .toLowerCase();
}

function photoSearchText(photo: GalleryImage): string {
  return [
    photo.title,
    photo.description,
    photo.alt_text,
    urlPathText(photo.image),
    urlPathText(photo.original_url),
    photo.source_page_urls,
  ]
    .filter((part): part is string => Boolean(part))
    .join(' ')
    .toLowerCase();
}

type KeywordMatch = {
  rank: number;
  /** Orders like images inside a rank (keyword position / canonical key). */
  subRank: number;
  keyword: string;
  length: number;
};

/** Collapse label variants so e.g. pool / swimming pool sort as one cluster. */
const KEYWORD_CANONICAL: Record<string, string> = {
  // Establishing / exterior
  signage: 'signage',
  'community sign': 'signage',
  'monument sign': 'signage',
  'entrance sign': 'signage',
  'entry sign': 'signage',
  'property sign': 'signage',
  'welcome sign': 'signage',
  'building sign': 'signage',
  monument: 'signage',
  'building exterior': 'exterior',
  'property exterior': 'exterior',
  exterior: 'exterior',
  facade: 'exterior',
  façade: 'exterior',
  'main entrance': 'exterior',
  'curb appeal': 'exterior',
  'street view': 'exterior',
  'building front': 'exterior',
  aerial: 'aerial',
  drone: 'aerial',
  "bird's eye": 'aerial',
  'birds eye': 'aerial',
  birdseye: 'aerial',
  overhead: 'aerial',
  skyline: 'aerial',
  // Outdoor amenities
  'swimming pool': 'pool',
  'indoor swimming': 'indoor pool',
  'indoor pool': 'indoor pool',
  'lap pool': 'indoor pool',
  pool: 'pool',
  barbecue: 'bbq',
  grilling: 'bbq',
  grill: 'bbq',
  firepit: 'fire pit',
  'sun deck': 'sundeck',
  greenspace: 'green space',
  'dog park': 'dog park',
  'pet park': 'dog park',
  jacuzzi: 'hot tub',
  // Indoor amenities
  'club house': 'clubhouse',
  'resident club': 'clubhouse',
  'amenity center': 'clubhouse',
  lounge: 'clubhouse',
  lobby: 'clubhouse',
  'game room': 'clubhouse',
  theater: 'clubhouse',
  leasing: 'clubhouse',
  concierge: 'clubhouse',
  package: 'clubhouse',
  'mail room': 'clubhouse',
  mailroom: 'clubhouse',
  'pool table': 'billiard',
  pooltable: 'billiard',
  billiards: 'billiard',
  billiard: 'billiard',
  'fitness center': 'fitness',
  fitness: 'fitness',
  gym: 'fitness',
  yoga: 'fitness',
  workout: 'fitness',
  'weight room': 'fitness',
  coworking: 'cowork',
  cowork: 'cowork',
  'cyber cafe': 'business center',
  'computer lab': 'business center',
  conference: 'business center',
  'business center': 'business center',
  'steam room': 'spa',
  massage: 'spa',
  sauna: 'spa',
  'pet spa': 'pet',
  'pet wash': 'pet',
  'dog wash': 'pet',
  'pet grooming': 'pet',
  'pet care': 'pet',
  'pet station': 'pet',
  'pet amenit': 'pet',
  pet: 'pet',
  // Unit interiors
  'washer and dryer': 'laundry',
  washer: 'laundry',
  dryer: 'laundry',
  laundry: 'laundry',
  'w/d': 'laundry',
  'walk-in': 'closet',
  wardrobe: 'closet',
  livingroom: 'living',
  'living room': 'living',
  'great room': 'living',
  'family room': 'living',
  dining: 'living',
  'bed room': 'bedroom',
  'master bedroom': 'bedroom',
  'primary bedroom': 'bedroom',
  'master suite': 'bedroom',
  'primary suite': 'bedroom',
  bedroom: 'bedroom',
  'bath room': 'bathroom',
  'master bath': 'bathroom',
  'primary bath': 'bathroom',
  'guest bath': 'bathroom',
  'full bath': 'bathroom',
  'half bath': 'bathroom',
  bathtub: 'bathroom',
  shower: 'bathroom',
  vanity: 'bathroom',
  restroom: 'bathroom',
  'powder room': 'bathroom',
  bath: 'bathroom',
  bathroom: 'bathroom',
  'flex room': 'office',
  'flex space': 'office',
  'work from home': 'office',
  den: 'office',
  study: 'office',
  paito: 'patio',
  balcony: 'patio',
  terrace: 'patio',
  deck: 'patio',
};

/**
 * Pick the subtype with the longest matching keyword so specific labels win
 * (e.g. "bathroom" over "bedroom", "clubhouse" over "building").
 */
function bestKeywordMatch(text: string, groups: string[][]): KeywordMatch {
  let best: KeywordMatch = {
    rank: groups.length,
    subRank: 0,
    keyword: '',
    length: 0,
  };

  for (let i = 0; i < groups.length; i += 1) {
    for (let j = 0; j < groups[i].length; j += 1) {
      const keyword = groups[i][j];
      if (!text.includes(keyword)) continue;
      if (keyword.length < best.length) continue;
      if (keyword.length === best.length && i > best.rank) continue;
      if (keyword.length === best.length && i === best.rank && j >= best.subRank) continue;
      best = { rank: i, subRank: j, keyword, length: keyword.length };
    }
  }

  return best;
}

function canonicalKeyword(keyword: string): string {
  return KEYWORD_CANONICAL[keyword] || keyword;
}

/**
 * Within a subtype rank, order by the earliest keyword that shares the same
 * canonical cluster (e.g. bbq / barbecue / grill all use the "bbq" slot).
 * Keeps synonym variants contiguous instead of scattering by raw keyword index.
 */
function clusterSortRank(match: KeywordMatch, groups: string[][]): number {
  if (!match.keyword || match.rank >= groups.length) return match.subRank;
  const canonical = canonicalKeyword(match.keyword);
  const group = groups[match.rank];
  for (let j = 0; j < group.length; j += 1) {
    if (canonicalKeyword(group[j]) === canonical) return j;
  }
  return match.subRank;
}

/** Remove billiard "pool table" phrases entirely (for swimming-pool detection). */
function textWithoutPoolTables(text: string): string {
  return text.replace(/pool\s*tables?/g, ' ').replace(/pooltables?/g, ' ');
}

/**
 * Rewrite billiard "pool table" phrases to "billiard" so keyword matching:
 * 1) does not treat them as swimming "pool", and
 * 2) cannot substring-match "indoor pool" inside "indoor pool table".
 */
function normalizePoolTableText(text: string): string {
  return text.replace(/pool\s*tables?/g, ' billiard ').replace(/pooltables?/g, ' billiard ');
}

/** True for swimming pools — not billiard / pool-table rooms. */
function isPoolText(text: string): boolean {
  const swimmingText = textWithoutPoolTables(text);
  return swimmingText.includes('pool') || swimmingText.includes('swimming');
}

/** Indoor pools must stay together — "indoor swimming pool" used to split across outdoor/indoor. */
function isIndoorPoolText(text: string): boolean {
  if (!isPoolText(text)) return false;
  const swimmingText = textWithoutPoolTables(text);
  if (swimmingText.includes('outdoor pool') || swimmingText.includes('outdoor swimming')) {
    return false;
  }
  return (
    swimmingText.includes('indoor pool') ||
    swimmingText.includes('indoor swimming') ||
    (swimmingText.includes('indoor') && isPoolText(text))
  );
}

/** Unit interiors — kitchen → living → office → bedroom → bath → laundry → closet → patio. */
const APARTMENT_SUBTYPE_KEYWORDS: string[][] = [
  ['kitchen', 'cooktop', 'stove', 'island'],
  ['living room', 'livingroom', 'living', 'great room', 'family room', 'dining'],
  ['office', 'den', 'study', 'flex room', 'flex space', 'work from home'],
  ['bedroom', 'bed room', 'master bedroom', 'primary bedroom', 'master suite', 'primary suite'],
  // Longest-keyword match still classifies "bathroom" over "bedroom" when both appear.
  ['bathroom', 'bath room', 'master bath', 'primary bath', 'guest bath', 'full bath', 'half bath', 'powder room', 'restroom', 'vanity', 'bathtub', 'shower', 'bath'],
  ['washer and dryer', 'washer', 'dryer', 'laundry', 'w/d', 'laundry room'],
  ['closet', 'wardrobe', 'walk-in'],
  ['patio', 'paito', 'balcony', 'terrace', 'deck'],
];

/**
 * Community / amenity shots — signage → exterior → aerial → outdoor →
 * indoor pool → clubhouse → fitness → business center → spa → pet →
 * other indoor. Unmatched community photos default to the generic indoor
 * catch-all.
 */
const COMMUNITY_OUTDOOR = 3;
/** Last establishing rank (signage / exterior / aerial / outdoor) before named amenities. */
const COMMUNITY_ESTABLISHING_MAX = COMMUNITY_OUTDOOR;
const COMMUNITY_INDOOR_POOL = 4;
const COMMUNITY_INDOOR_OTHER = 10;
/**
 * Short amenity tokens ("lounge", "lobby") must not pull outdoor shots into
 * clubhouse when a longer establishing keyword already matched.
 */
const MIN_NAMED_AMENITY_OVERRIDE_LEN = 7;

const COMMUNITY_SUBTYPE_KEYWORDS: string[][] = [
  // Community signage first — brand the property before building exteriors.
  [
    'monument sign',
    'community sign',
    'entrance sign',
    'entry sign',
    'property sign',
    'welcome sign',
    'building sign',
    'signage',
    'monument',
  ],
  // Building exterior — establish the property before amenities.
  [
    'building exterior',
    'property exterior',
    'exterior',
    'facade',
    'façade',
    'main entrance',
    'curb appeal',
    'street view',
    'building front',
  ],
  ['aerial', 'drone', 'bird\'s eye', 'birds eye', 'birdseye', 'overhead', 'skyline'],
  // Outdoor: pool keywords first so outdoor pools cluster, then other amenities.
  [
    'swimming pool',
    'pool',
    'outdoor amenit',
    'courtyard',
    'playground',
    'dog park',
    'pet park',
    'bbq',
    'barbecue',
    'grilling',
    'grill',
    'fire pit',
    'firepit',
    'picnic',
    'gazebo',
    'pergola',
    'cabana',
    'hot tub',
    'jacuzzi',
    'splash',
    'tennis',
    'pickleball',
    'basketball',
    'sport court',
    'sundeck',
    'sun deck',
    'green space',
    'greenspace',
    'garden',
    'fountain',
    'outdoor',
  ],
  // Indoor pools — kept as their own group so they never mix with outdoor pools
  // or generic "indoor" catch-all shots.
  ['indoor pool', 'indoor swimming', 'lap pool'],
  [
    'clubhouse',
    'club house',
    'resident club',
    'amenity center',
    'lounge',
    'lobby',
    'game room',
    'theater',
    // Billiards — "pool table" must beat bare outdoor "pool" (longest-keyword match).
    'pool table',
    'pooltable',
    'billiards',
    'billiard',
    'leasing',
    'concierge',
    'package',
    'mail room',
    'mailroom',
  ],
  ['fitness center', 'fitness', 'gym', 'yoga', 'workout', 'weight room'],
  ['business center', 'conference', 'cowork', 'coworking', 'cyber cafe', 'computer lab'],
  ['spa', 'sauna', 'steam room', 'massage'],
  [
    'pet spa',
    'pet wash',
    'dog wash',
    'pet grooming',
    'pet care',
    'pet station',
    'pet amenit',
    'pet',
  ],
  // Remaining indoor / generic amenity shots (do not include bare "indoor" —
  // that steals indoor pool photos; pools are handled above).
  ['indoor amenit', 'media room', 'amenit'],
];

const BUCKET_ORDER = { community: 0, apartment: 1, other: 2 } as const;

/**
 * Longest-keyword alone lets "building exterior" / "outdoor amenit" steal
 * clubhouse & fitness shots. Prefer a clear named-amenity label when present.
 */
function preferNamedAmenityOverEstablishing(
  match: KeywordMatch,
  text: string,
): KeywordMatch {
  if (match.rank > COMMUNITY_ESTABLISHING_MAX) return match;

  const amenityGroups = COMMUNITY_SUBTYPE_KEYWORDS.slice(COMMUNITY_INDOOR_POOL);
  const amenityMatch = bestKeywordMatch(text, amenityGroups);
  if (amenityMatch.rank >= amenityGroups.length) return match;
  if (amenityMatch.length < MIN_NAMED_AMENITY_OVERRIDE_LEN) return match;

  return {
    rank: amenityMatch.rank + COMMUNITY_INDOOR_POOL,
    subRank: amenityMatch.subRank,
    keyword: amenityMatch.keyword,
    length: amenityMatch.length,
  };
}

/** Raw community match — no default rank (used for apartment vs community inference). */
function communityKeywordMatchRaw(text: string): KeywordMatch {
  if (isIndoorPoolText(text)) {
    return {
      rank: COMMUNITY_INDOOR_POOL,
      subRank: 0,
      keyword: 'indoor pool',
      length: 11,
    };
  }

  // Pool + named amenity (e.g. "clubhouse pool deck") — keep the amenity group.
  // Strip pool-table phrases so billiards wording cannot pull a real swimming-pool
  // shot out of the outdoor bucket when both appear in the same caption.
  if (isPoolText(text)) {
    const amenitySearch = textWithoutPoolTables(text);
    const amenityGroups = COMMUNITY_SUBTYPE_KEYWORDS.slice(COMMUNITY_INDOOR_POOL + 1);
    const amenityMatch = bestKeywordMatch(amenitySearch, amenityGroups);
    if (
      amenityMatch.rank < amenityGroups.length &&
      amenityMatch.length >= MIN_NAMED_AMENITY_OVERRIDE_LEN
    ) {
      return {
        rank: amenityMatch.rank + COMMUNITY_INDOOR_POOL + 1,
        subRank: amenityMatch.subRank,
        keyword: amenityMatch.keyword,
        length: amenityMatch.length,
      };
    }
    return {
      rank: COMMUNITY_OUTDOOR,
      subRank: 0,
      keyword: 'pool',
      length: 4,
    };
  }

  // Rewrite pool-table → billiard so "indoor pool table" does not match "indoor pool".
  const normalized = normalizePoolTableText(text);
  return preferNamedAmenityOverEstablishing(
    bestKeywordMatch(normalized, COMMUNITY_SUBTYPE_KEYWORDS),
    normalized,
  );
}

/**
 * Community subtype for sorting: prefer title/alt/description matches so CDN
 * paths like "building-exterior-clubhouse.jpg" cannot reshuffle labeled shots.
 */
function communityKeywordMatchForPhoto(photo: GalleryImage): KeywordMatch {
  const labelText = photoLabelText(photo);
  if (labelText.trim()) {
    const labelMatch = communityKeywordMatchRaw(labelText);
    if (labelMatch.rank < COMMUNITY_SUBTYPE_KEYWORDS.length) {
      return labelMatch;
    }
  }

  const match = communityKeywordMatchRaw(photoSearchText(photo));
  if (match.rank >= COMMUNITY_SUBTYPE_KEYWORDS.length) {
    return {
      rank: COMMUNITY_INDOOR_OTHER,
      subRank: 0,
      keyword: '',
      length: 0,
    };
  }
  return match;
}

/**
 * Infer apartment vs community when the title is not already labeled.
 * Room-type keywords lean unit; amenity/exterior/aerial lean community.
 */
function inferPhotoBucket(photo: GalleryImage): 'apartment' | 'community' | 'other' {
  const labeled = classifyPhoto(photo);
  if (labeled !== 'other') return labeled;

  const text = photoSearchText(photo);
  if (!text.trim()) return 'other';

  // Pool shots are always community amenities.
  if (isPoolText(text)) return 'community';

  const apartmentHit = bestKeywordMatch(text, APARTMENT_SUBTYPE_KEYWORDS);
  const communityHit = communityKeywordMatchRaw(text);
  const apartmentMatched = apartmentHit.rank < APARTMENT_SUBTYPE_KEYWORDS.length;
  const communityMatched = communityHit.rank < COMMUNITY_SUBTYPE_KEYWORDS.length;

  if (apartmentMatched && !communityMatched) return 'apartment';
  if (communityMatched && !apartmentMatched) return 'community';
  if (apartmentMatched && communityMatched) {
    if (communityHit.length > apartmentHit.length) return 'community';
    return 'apartment';
  }
  return 'other';
}

function subtypeSortKeys(
  photo: GalleryImage,
  bucket: 'apartment' | 'community' | 'other',
): { subtypeRank: number; clusterRank: number; clusterKey: string } {
  const text = photoSearchText(photo);

  if (bucket === 'apartment') {
    const match = bestKeywordMatch(text, APARTMENT_SUBTYPE_KEYWORDS);
    return {
      subtypeRank: match.rank,
      clusterRank: clusterSortRank(match, APARTMENT_SUBTYPE_KEYWORDS),
      clusterKey: canonicalKeyword(match.keyword),
    };
  }

  if (bucket === 'community') {
    const match = communityKeywordMatchForPhoto(photo);
    return {
      subtypeRank: match.rank,
      clusterRank: clusterSortRank(match, COMMUNITY_SUBTYPE_KEYWORDS),
      clusterKey: canonicalKeyword(match.keyword),
    };
  }

  return { subtypeRank: 0, clusterRank: 0, clusterKey: '' };
}

/**
 * Sort gallery photos: community/amenity shots first (signage → exterior →
 * aerial → outdoor → indoor pool → clubhouse → fitness → business center →
 * spa → pet → other indoor), then unit interiors (grouped by room type).
 * Within each group, like labels cluster together. Stable for equal ranks.
 */
export function sortGalleryImages(images: GalleryImage[]): GalleryImage[] {
  return images
    .map((image, index) => {
      const bucket = inferPhotoBucket(image);
      const keys = subtypeSortKeys(image, bucket);
      return {
        image,
        index,
        bucketRank: BUCKET_ORDER[bucket],
        subtypeRank: keys.subtypeRank,
        clusterRank: keys.clusterRank,
        clusterKey: keys.clusterKey,
      };
    })
    .sort((a, b) => {
      if (a.bucketRank !== b.bucketRank) return a.bucketRank - b.bucketRank;
      if (a.subtypeRank !== b.subtypeRank) return a.subtypeRank - b.subtypeRank;
      if (a.clusterRank !== b.clusterRank) return a.clusterRank - b.clusterRank;
      if (a.clusterKey !== b.clusterKey) return a.clusterKey.localeCompare(b.clusterKey);
      return a.index - b.index;
    })
    .map((entry) => entry.image);
}
